/**
 * Google Drive sync utilities.
 *
 * Stores budget data in the Drive "appDataFolder" — a hidden space only
 * accessible to this app. It does NOT appear in the user's main Drive folder.
 *
 * OAuth uses Google Identity Services (GIS), loaded lazily so it doesn't
 * affect page load when Drive sync is not used.
 *
 * GUARDRAILS FOR FUTURE AGENTS:
 * - Never load the GIS script in index.html — it is loaded lazily here.
 * - Tokens live in module-level variables ONLY — never store them in
 *   localStorage (they expire in 1 hour and must not leak across sessions).
 * - The file name in appDataFolder is always 'budget-data.json'.
 * - Use writeToDrive() / readFromDrive() for all Drive I/O — never call the
 *   Drive REST API directly from components or BudgetContext.
 * - err.message === 'NO_TOKEN' is the sentinel for "token expired" — handle
 *   it by setting a reconnect flag, not by calling requestToken() directly.
 */

const GIS_SRC   = 'https://accounts.google.com/gsi/client';
const SCOPES    = 'https://www.googleapis.com/auth/drive.appdata https://www.googleapis.com/auth/userinfo.email';
const FILE_NAME = 'budget-data.json';

// ─── GIS lazy loader ──────────────────────────────────────────────────────────

let _gisLoadPromise = null;

/** Load the Google Identity Services script (no-op if already loaded). */
export function loadGIS() {
  if (_gisLoadPromise) return _gisLoadPromise;
  _gisLoadPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return reject(new Error('Not in browser'));
    if (window.google?.accounts?.oauth2) return resolve();
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload  = resolve;
    script.onerror = () => {
      _gisLoadPromise = null; // allow retry
      reject(new Error('Failed to load Google Identity Services — check internet connection'));
    };
    document.head.appendChild(script);
  });
  return _gisLoadPromise;
}

// ─── Token management ─────────────────────────────────────────────────────────

let _tokenClient  = null;
let _accessToken  = null;
let _tokenExpiry  = 0; // unix ms
let _pendingTokenResolvers = []; // Array<{ resolve, reject }>

/** True when we hold a non-expired access token. */
export function hasValidToken() {
  return !!_accessToken && Date.now() < _tokenExpiry;
}

export function getAccessToken() {
  return hasValidToken() ? _accessToken : null;
}

function storeToken(resp) {
  _accessToken = resp.access_token;
  _tokenExpiry = Date.now() + (Number(resp.expires_in) - 60) * 1000; // 60 s buffer
}

/**
 * Initialise the GIS token client. Call after loadGIS() resolves.
 * @param {string}   clientId - Google OAuth 2.0 Client ID
 * @param {function} onToken  - called with tokenResponse (success) or null (error/cancel)
 */
export function initTokenClient(clientId, onToken) {
  // Reject any resolvers from a previous token client so their stale GIS
  // callbacks can't interfere with the new request we're about to make.
  if (_pendingTokenResolvers.length) {
    _pendingTokenResolvers.forEach(({ reject }) => reject(new Error('client_reinit')));
    _pendingTokenResolvers = [];
  }
  _tokenClient = window.google.accounts.oauth2.initTokenClient({
    client_id: clientId,
    scope: SCOPES,
    callback: (resp) => {
      if (resp.error) {
        onToken(null);
        _pendingTokenResolvers.forEach(({ reject }) => reject(new Error(resp.error)));
        _pendingTokenResolvers = [];
        return;
      }
      storeToken(resp);
      onToken(resp);
      _pendingTokenResolvers.forEach(({ resolve }) => resolve(resp));
      _pendingTokenResolvers = [];
    },
    // GIS calls this for popup-level failures (blocked, or closed by the user).
    // Using it instead of a wall-clock timer means a slow-but-successful login is
    // never mistaken for a failure. err.type is
    // 'popup_failed_to_open' | 'popup_closed' | 'unknown'.
    error_callback: (err) => {
      onToken(null);
      const e = new Error(err?.type || 'oauth_error');
      _pendingTokenResolvers.forEach(({ reject }) => reject(e));
      _pendingTokenResolvers = [];
    },
  });
  return _tokenClient;
}

/**
 * Request an access token and return a Promise.
 * @param {boolean} selectAccount - true = show account picker (first-time connect)
 * @param {number}  timeoutMs     - safety net only; real cancel/blocked cases are
 *   reported by GIS's error_callback. Generous so a slow human login never trips it.
 */
export function requestTokenAsync(selectAccount = false, timeoutMs = 180000) {
  return new Promise((resolve, reject) => {
    if (!_tokenClient) { reject(new Error('Token client not initialised — call initTokenClient first')); return; }
    const timer = setTimeout(() => {
      _pendingTokenResolvers = _pendingTokenResolvers.filter(r => r.resolve !== resolve);
      reject(new Error('TOKEN_TIMEOUT'));
    }, timeoutMs);
    _pendingTokenResolvers.push({
      resolve: (resp) => { clearTimeout(timer); resolve(resp); },
      reject:  (err)  => { clearTimeout(timer); reject(err); },
    });
    _tokenClient.requestAccessToken(selectAccount ? { prompt: 'select_account' } : {});
  });
}

/** Returns milliseconds until the current token expires, or 0 if no token. */
export function msUntilTokenExpiry() {
  if (!_accessToken) return 0;
  return Math.max(0, _tokenExpiry - Date.now());
}

/**
 * Request an access token.
 * @param {boolean} selectAccount - true = always show account picker (first-time connect)
 */
export function requestToken(selectAccount = false) {
  if (!_tokenClient) throw new Error('Token client not initialised — call initTokenClient first');
  _tokenClient.requestAccessToken(selectAccount ? { prompt: 'select_account' } : {});
}

/** Revoke the current token and wipe local state. */
export function revokeToken() {
  if (_accessToken) {
    try { window.google?.accounts?.oauth2?.revoke(_accessToken, () => {}); } catch {}
  }
  _accessToken  = null;
  _tokenExpiry  = 0;
  _tokenClient  = null;
}

// ─── Drive REST API helpers ───────────────────────────────────────────────────

/** Parse a Drive API response body safely, with a descriptive error on empty/invalid JSON. */
async function parseJSON(res, label) {
  const text = await res.text();
  if (!text.trim()) throw new Error(`Empty response from Google (${label}) — the Drive API may not be enabled in your Cloud project`);
  try { return JSON.parse(text); }
  catch { throw new Error(`Invalid JSON from Google (${label}): ${text.slice(0, 120)}`); }
}

async function driveRequest(path, options = {}) {
  const token = getAccessToken();
  if (!token) throw new Error('NO_TOKEN');

  const url = path.startsWith('https://') ? path : `https://www.googleapis.com${path}`;
  const res = await fetch(url, {
    ...options,
    headers: { Authorization: `Bearer ${token}`, ...(options.headers || {}) },
  });

  if (res.status === 401) throw new Error('NO_TOKEN'); // treat expired token uniformly
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error?.message || `Drive API error ${res.status}`);
  }
  return res;
}

/** Return the file ID of budget-data.json in appDataFolder, or null. */
async function findFileId() {
  const res = await driveRequest(
    `/drive/v3/files?spaces=appDataFolder&q=name%3D'${FILE_NAME}'&fields=files(id)&pageSize=1`
  );
  const { files } = await parseJSON(res, 'files.list');
  return files?.[0]?.id ?? null;
}

/** Create budget-data.json in appDataFolder. Returns the new file ID. */
async function createFile() {
  const res = await driveRequest('/drive/v3/files', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: FILE_NAME,
      parents: ['appDataFolder'],
      mimeType: 'application/json',
    }),
  });
  const { id } = await parseJSON(res, 'files.create');
  return id;
}

/**
 * Read and parse a budget file from appDataFolder.
 * @param {string|null} fileId - if provided, fetches directly without a name search
 * @returns {object|null}
 */
export async function readFromDrive(fileId = null) {
  const id = fileId || await findFileId();
  if (!id) return null;
  try {
    const res = await driveRequest(`/drive/v3/files/${id}?alt=media`);
    return parseJSON(res, 'files.get');
  } catch (err) {
    if (/404|not found/i.test(err.message)) return null; // file deleted externally
    throw err;
  }
}

/**
 * Write data to a budget file in appDataFolder (creates budget-data.json if no id given).
 * @param {object}      data
 * @param {string|null} fileId - if provided, patches that file directly
 */
export async function writeToDrive(data, fileId = null) {
  let id = fileId || await findFileId();
  if (!id) id = await createFile();
  await driveRequest(
    `https://www.googleapis.com/upload/drive/v3/files/${id}?uploadType=media`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data, null, 2),
    }
  );
}

/**
 * List all budget files in appDataFolder.
 * @returns {Array<{ id: string, name: string, modifiedTime: string }>}
 */
export async function listDriveFiles() {
  const res = await driveRequest(
    `/drive/v3/files?spaces=appDataFolder&fields=files(id,name,modifiedTime)&pageSize=20`
  );
  const { files } = await parseJSON(res, 'files.list');
  return files || [];
}

/**
 * Create a new named budget file in appDataFolder. Returns the new file ID.
 * @param {string} name - display name; .json is appended if missing
 */
export async function createNamedFile(name) {
  const fileName = name.endsWith('.json') ? name : `${name}.json`;
  const res = await driveRequest('/drive/v3/files', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: fileName, parents: ['appDataFolder'], mimeType: 'application/json' }),
  });
  const { id } = await parseJSON(res, 'files.create');
  return id;
}

/**
 * Rename an existing Drive file.
 * @param {string} fileId
 * @param {string} newName - .json is appended if missing
 */
export async function renameDriveFile(fileId, newName) {
  const fileName = newName.endsWith('.json') ? newName : `${newName}.json`;
  await driveRequest(`/drive/v3/files/${fileId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: fileName }),
  });
}

/** Return the email address of the currently signed-in user, or null on failure. */
export async function getUserEmail() {
  try {
    const res = await driveRequest('/oauth2/v3/userinfo');
    const { email } = await parseJSON(res, 'userinfo');
    return email || null;
  } catch {
    return null; // email is display-only; don't block Drive sync if userinfo fails
  }
}
